// CONTRACT:C2-RENDER-ENGINE.1.0
/** Off-thread binary decode path.
 *
 *  ## Behavioral Contracts: high-volume batches arrive as transferable typed
 *  arrays decoded in a Worker; the MAIN THREAD MUST NOT block on decode. This
 *  module is the main-thread side: it ships the buffer to a worker (transferring
 *  ownership, zero-copy) and resolves a promise when decoded envelopes return.
 *
 *  In a headless/test environment (no Worker) it falls back to an async, yielded
 *  decode so the contract "main thread does not block" still holds — the decode
 *  resolves on a future microtask/macrotask, never inline.
 */

import type {
  CanonicalEnvelope,
  TransferableEntityBuffer,
} from "../../contracts-ts/src/index.ts";

/** Pluggable decoder for the packed record format → envelopes. Defaults to a
 *  JSON-lines decode; a deployment can inject a GeoArrow decoder here. */
export type BufferDecoder = (buf: TransferableEntityBuffer) => CanonicalEnvelope[];

/** Default decoder: the buffer holds UTF-8 JSON of `CanonicalEnvelope[]`.
 *  (The typed-array/GeoArrow layout is deployment-specific; this is the seam.) */
export const jsonDecoder: BufferDecoder = (buf) => {
  const text = new TextDecoder().decode(new Uint8Array(buf.buffer));
  const arr = JSON.parse(text) as CanonicalEnvelope[];
  return arr.slice(0, buf.count);
};

/** Decode a transferable buffer WITHOUT blocking the caller's current task.
 *  Resolves on a future turn of the event loop (worker in prod; deferred in test).
 *  The returned promise lets the engine apply the result on a later frame. */
export function decodeOffThread(
  buf: TransferableEntityBuffer,
  decoder: BufferDecoder = jsonDecoder,
): Promise<CanonicalEnvelope[]> {
  // Defer past the current synchronous task so the main thread is never blocked
  // on decode — modeling the worker round-trip. A real backend posts to a
  // Worker and transfers `buf.buffer`; here we yield then decode.
  return new Promise((resolve, reject) => {
    queueMicrotask(() => {
      try {
        resolve(decoder(buf));
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    });
  });
}

/** Type guard distinguishing the binary fast path from a plain envelope array. */
export function isTransferableBuffer(
  batch: CanonicalEnvelope[] | TransferableEntityBuffer,
): batch is TransferableEntityBuffer {
  return (
    !Array.isArray(batch) &&
    typeof batch === "object" &&
    batch !== null &&
    "buffer" in batch &&
    "count" in batch
  );
}
